import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { BrandLogoComponent } from '../../../types/builder';
import { useBuilderStore } from '../../../store/builderStore';
import { ColorPicker } from '../../Common/ColorPicker';
import { AlignLeft, AlignCenter, AlignRight, Upload, Loader, ExternalLink } from 'lucide-react';
import toast from 'react-hot-toast';

interface BrandLogoSettingsProps {
  component: BrandLogoComponent;
}

export const BrandLogoSettings: React.FC<BrandLogoSettingsProps> = ({ component }) => {
  const { t } = useTranslation('builder');
  const { updateComponent } = useBuilderStore();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);

  const handleUpdate = (updates: Partial<BrandLogoComponent['settings']>) => {
    updateComponent(component.id, {
      settings: { ...component.settings, ...updates },
    });
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error(t('settings.common.imageInvalidType'));
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error(t('settings.common.imageTooLarge'));
      return;
    }

    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append('logo', file);

      const token = localStorage.getItem('token');
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

      const response = await fetch(`${API_URL}/upload/logo`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error(t('settings.common.uploadFailed'));
      }

      const data = await response.json();
      handleUpdate({ logoUrl: data.url });
      toast.success(t('settings.brandLogo.uploadSuccess'));
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(t('settings.brandLogo.uploadFailedSpecific'));
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.brandLogo.styleLabel')}</label>
        <select
          value={component.settings.style || 'default'}
          onChange={(e) => handleUpdate({ style: e.target.value as any })}
          className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="default">{t('settings.common.styles.default')}</option>
          <option value="modern">{t('settings.common.styles.modern')}</option>
          <option value="minimal">{t('settings.common.styles.minimal')}</option>
          <option value="card">{t('settings.common.styles.card')}</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.brandLogo.label')}</label>
        
        {/* Logo preview */}
        {component.settings.logoUrl && (
          <div className="mb-3">
            <img
              src={component.settings.logoUrl}
              alt="Logo preview"
              className="max-w-xs max-h-32 object-contain border border-gray-300 rounded"
              onError={(e) => {
                (e.target as HTMLImageElement).src = 'https://via.placeholder.com/300x150?text=Logo';
              }}
            />
          </div>
        )}

        <div className="space-y-2">
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isUploading ? (
              <>
                <Loader className="w-4 h-4 animate-spin" />
                {t('settings.common.uploading')}
              </>
            ) : (
              <>
                <Upload className="w-4 h-4" />
                {t('settings.brandLogo.uploadCta')}
              </>
            )}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileUpload}
            className="hidden"
          />
          <p className="text-xs text-gray-500">
            {t('settings.brandLogo.uploadHint')}<br />
            {t('settings.brandLogo.sizeHint')}<br />
            {t('settings.brandLogo.maxSizeHint')}
          </p>
        </div>

        <div className="mt-3">
          <label className="block text-xs font-medium text-gray-700 mb-1">
            {t('settings.brandLogo.orPasteUrl')}
          </label>
          <input
            type="text"
            value={component.settings.logoUrl}
            onChange={(e) => handleUpdate({ logoUrl: e.target.value })}
            className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="https://example.com/logo.png"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">
          {t('settings.brandLogo.logoSize', { value: component.settings.logoSize })}
        </label>
        <input
          type="range"
          min="50"
          max="400"
          value={component.settings.logoSize}
          onChange={(e) => handleUpdate({ logoSize: parseInt(e.target.value) })}
          className="w-full"
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">
          <ExternalLink className="w-4 h-4 inline mr-1" />
          {t('settings.brandLogo.linkUrlOptional')}
        </label>
        <input
          type="text"
          value={component.settings.link}
          onChange={(e) => handleUpdate({ link: e.target.value })}
          className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder={t('settings.brandLogo.linkPlaceholder')}
        />
      </div>

      {component.settings.link && (
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="openInNewTab"
            checked={component.settings.openInNewTab}
            onChange={(e) => handleUpdate({ openInNewTab: e.target.checked })}
            className="w-4 h-4"
          />
          <label htmlFor="openInNewTab" className="text-sm font-medium">
            {t('settings.brandLogo.openInNewTab')}
          </label>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.common.alignment')}</label>
        <div className="flex gap-2">
          <button
            onClick={() => handleUpdate({ alignment: 'left' })}
            className={`flex-1 p-3 rounded-lg border-2 transition-colors ${
              component.settings.alignment === 'left'
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <AlignLeft className="w-5 h-5 mx-auto" />
          </button>
          <button
            onClick={() => handleUpdate({ alignment: 'center' })}
            className={`flex-1 p-3 rounded-lg border-2 transition-colors ${
              component.settings.alignment === 'center'
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <AlignCenter className="w-5 h-5 mx-auto" />
          </button>
          <button
            onClick={() => handleUpdate({ alignment: 'right' })}
            className={`flex-1 p-3 rounded-lg border-2 transition-colors ${
              component.settings.alignment === 'right'
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <AlignRight className="w-5 h-5 mx-auto" />
          </button>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.common.backgroundColor')}</label>
        <ColorPicker
          color={component.settings.backgroundColor}
          onChange={(color) => handleUpdate({ backgroundColor: color })}
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">
          {t('settings.common.paddingPx', { value: component.settings.padding })}
        </label>
        <input
          type="range"
          min="0"
          max="100"
          value={component.settings.padding}
          onChange={(e) => handleUpdate({ padding: parseInt(e.target.value) })}
          className="w-full"
        />
      </div>
    </div>
  );
};

